import { Badge } from '@/components/ui/badge';
import { Check, Minus, Plus } from 'lucide-react';
import { OptionType } from '@/types';
import type { MenuOption } from '@/types';
import type { SelectedOptionsState, ValidationErrorsState, InputValuesState } from '@/hooks/useMenuOptionState';
import { resolveCustomerDisplayName } from '@/utils/displayName';

// 옵션 타입 정보 인터페이스
interface OptionTypeInfo {
  label: string;
  color: string;
  description: string;
  themeColor: string;
}

interface OptionGroupSectionProps {
  optionGroup: MenuOption;
  selectedOptions: SelectedOptionsState;
  validationErrors: ValidationErrorsState;
  /** #493: 입력형 옵션 사용자 입력 값 (number_input/text_input/textarea) */
  inputValues?: InputValuesState;
  /** #493: 입력형 옵션 값 변경 핸들러 */
  onInputChange?: (optionGroupId: string, value: string) => void;
  /**
   * #499: 입력형 옵션 validation_rules (BE ValidationRules).
   * { min_value, max_value, min_length, max_length, pattern } — UI 속성 + 검증.
   */
  validationRules?: {
    min_length?: number;
    max_length?: number;
    min_value?: number;
    max_value?: number;
    pattern?: string;
  };
  translations: {
    optionGroups: Record<string, string>;
    optionGroupDescriptions?: Record<string, string>;
    options: Record<string, string>;
    optionTypes?: {
      required?: string;
      optional?: string;
      requiredSingleDescription?: string;
      optionalSingleDescription?: string;
      multiUnlimitedDescription?: string;
      multiExactDescription?: string;
      multiRangeDescription?: string;
    };
    selectionStatus?: {
      totalCount?: string;
      selectedCount?: string;
    };
    /**
     * #394 Stage 4c — 검증 에러 메시지 i18n 키.
     * 부재 시 한국어 fallback (silent drift 차단 — 정상 흐름에서는 menuOption.ts es 블록 사용).
     */
    pleaseSelectRequired?: string;
    pleaseSelectExactly?: string;
    pleaseSelectMinimum?: string;
    pleaseSelectOption?: string;
    maximumSelection?: string;
    /**
     * #492 (A1): 입력형 옵션 (number_input/text_input/textarea) 안내 메시지.
     * R2 codex P1 시정: required (nullable 시 안내 통째 미노출 silent UI failure 차단).
     * #493 fully orderable 진입 후 정식 입력 UI 로 대체.
     */
    inputTypePending: string;
  };
  formatPrice: (amount: number) => string;
  onOptionSelect: (optionGroup: MenuOption, optionId: string) => void;
  onOptionDecrement?: (optionGroup: MenuOption, optionId: string) => void;
}

export function OptionGroupSection({
  optionGroup,
  selectedOptions,
  validationErrors,
  inputValues,
  onInputChange,
  validationRules,
  translations,
  formatPrice,
  onOptionSelect,
  onOptionDecrement,
}: OptionGroupSectionProps) {
  const t = translations;
  const currentSelections = selectedOptions[optionGroup.id] || [];
  const selectedCount = currentSelections.length;
  const hasValidationError = validationErrors[optionGroup.id];
  const minSelection = optionGroup.minSelection || 0;
  const maxSelection = optionGroup.maxSelection || 999;

  // 검증 상태 확인
  const isUnderMin = selectedCount < minSelection;
  const isOverMax = selectedCount > maxSelection;
  const hasError = hasValidationError || isUnderMin || isOverMax;

  // 구체적인 에러 메시지 생성
  const getValidationErrorMessage = () => {
    if (hasValidationError) return hasValidationError;

    if (isUnderMin) {
      // #394 Stage 4c — 하드코딩된 영어/스페인어 fallback 제거. menuOption.ts i18n 키 사용.
      // 악센트 정합 (opción / máximo) + 영어 잔존 차단 + 단일 진실 공급원.
      switch (optionGroup.type) {
        case OptionType.REQUIRED_SINGLE:
          return t.pleaseSelectRequired || '필수 옵션을 선택해주세요';
        case OptionType.MULTI_EXACT:
          return (t.pleaseSelectExactly || '정확히 {count}개 선택해주세요').replace('{count}', minSelection.toString());
        case OptionType.MULTI_RANGE:
          return (t.pleaseSelectMinimum || '최소 {count}개 선택해주세요').replace('{count}', minSelection.toString());
        default:
          return t.pleaseSelectOption || '옵션을 선택해주세요';
      }
    }

    if (isOverMax) {
      return (t.maximumSelection || '최대 {max}개까지만 선택 가능합니다').replace('{max}', maxSelection.toString());
    }

    return null;
  };

  const validationErrorMessage = getValidationErrorMessage();

  // 옵션 타입별 표시 정보
  const getOptionTypeInfo = (): OptionTypeInfo => {
    switch (optionGroup.type) {
      case OptionType.REQUIRED_SINGLE:
        return {
          label: t.optionTypes?.required || '필수',
          color: 'bg-secondary',
          description: t.optionTypes?.requiredSingleDescription || '1개 필수 선택',
          themeColor: 'var(--secondary)'
        };
      case OptionType.OPTIONAL_SINGLE:
        return {
          label: t.optionTypes?.optional || '선택',
          color: 'bg-primary',
          description: t.optionTypes?.optionalSingleDescription || '0~1개 선택',
          themeColor: 'var(--primary)'
        };
      case OptionType.MULTI_UNLIMITED:
        return {
          label: t.optionTypes?.optional || '선택',
          color: 'bg-primary',
          description: t.optionTypes?.multiUnlimitedDescription || '무제한 선택',
          themeColor: 'var(--primary)'
        };
      case OptionType.MULTI_EXACT:
        return {
          label: t.optionTypes?.required || '필수',
          color: 'bg-secondary',
          description: (t.optionTypes?.multiExactDescription || '정확히 {count}개').replace('{count}', (optionGroup.exactSelection || minSelection).toString()),
          themeColor: 'var(--secondary)'
        };
      case OptionType.MULTI_RANGE:
        return {
          label: t.optionTypes?.required || '필수',
          color: 'bg-secondary',
          description: (t.optionTypes?.multiRangeDescription || '{min}~{max}개 선택').replace('{min}', minSelection.toString()).replace('{max}', maxSelection.toString()),
          themeColor: 'var(--secondary)'
        };
      default:
        return {
          label: t.optionTypes?.optional || '선택',
          color: 'bg-primary',
          description: '',
          themeColor: 'var(--primary)'
        };
    }
  };

  const typeInfo = getOptionTypeInfo();
  // 필수 옵션인지 선택 옵션인지 구분
  const isRequiredOption = optionGroup.type === OptionType.REQUIRED_SINGLE || optionGroup.type === OptionType.MULTI_EXACT || optionGroup.type === OptionType.MULTI_RANGE;
  const optionThemeColor = isRequiredOption ? 'var(--secondary)' : 'var(--primary)';
  const optionGroupName = resolveCustomerDisplayName(
    optionGroup._name,
    t.optionGroups?.[optionGroup.nameKey as keyof typeof t.optionGroups],
    optionGroup.nameKey,
  ) ?? optionGroup.id;
  const optionGroupDescription = optionGroup.descriptionKey
    ? resolveCustomerDisplayName(
      t.optionGroupDescriptions?.[optionGroup.descriptionKey as keyof typeof t.optionGroupDescriptions],
      optionGroup.descriptionKey,
    )
    : undefined;

  // #492 (A1): 입력형 옵션 (number_input / text_input / textarea) 분기.
  // 옵션 그룹 자체는 노출되되, 입력 UI 는 #493 에서 fully orderable 추가 예정.
  // 현재는 안내 메시지만 노출 (silent 누락 차단).
  const isInputTypeOption =
    optionGroup.type === OptionType.NUMBER_INPUT
    || optionGroup.type === OptionType.TEXT_INPUT
    || optionGroup.type === OptionType.TEXTAREA;

  return (
    <div
      id={`option-group-${optionGroup.id}`}
      className={`scroll-mt-[clamp(80px,20vw,120px)] transition-all duration-300 ${
        hasError ? 'rounded-xl p-[clamp(8px,2vw,12px)] bg-destructive/5' : ''
      }`}
    >
      {/* 옵션 그룹 헤더 */}
      <div className="flex items-start justify-between mb-[clamp(12px,3vw,16px)] min-[401px]:mb-[13px] gap-[clamp(8px,2vw,12px)] min-[401px]:gap-[10px]">
        <h3 className={`text-[clamp(15px,3.8vw,18px)] min-[400px]:text-[18px] min-[401px]:text-[14px] font-semibold flex-1 min-w-0 transition-colors ${
          hasError ? 'text-destructive' : 'text-foreground'
        }`}>
          {optionGroupName}
          {optionGroupDescription && (
            <span className="text-[clamp(12px,3vw,14px)] min-[400px]:text-[14px] min-[401px]:text-[11px] text-muted-foreground font-normal ml-[clamp(4px,1vw,8px)] min-[401px]:ml-[6px] block sm:inline">
              ({optionGroupDescription})
            </span>
          )}
        </h3>
        <Badge className={`text-[clamp(9px,2.2vw,11px)] min-[400px]:text-[11px] min-[401px]:text-[9px] px-[clamp(8px,2vw,12px)] min-[401px]:px-[10px] py-[clamp(3px,0.8vw,4px)] min-[401px]:py-[3px] font-medium rounded-full shrink-0 ${typeInfo.color} text-white`}>
          {typeInfo.label}
        </Badge>
      </div>

      {/* 검증 에러 메시지 */}
      {validationErrorMessage && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-[clamp(10px,2.5vw,12px)] min-[401px]:p-[10px] mb-[clamp(12px,3vw,16px)] min-[401px]:mb-[13px] animate-pulse">
          <p className="text-destructive text-[clamp(11px,2.8vw,13px)] min-[400px]:text-[13px] min-[401px]:text-[10px] font-medium">
            {validationErrorMessage}
          </p>
        </div>
      )}

      {/* #492 A1 fallback: onInputChange 미제공 시 안내 메시지 (legacy 사용처 호환) */}
      {isInputTypeOption && !onInputChange && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-[clamp(10px,2.5vw,12px)] mt-[clamp(8px,2vw,12px)]">
          <p className="text-amber-800 dark:text-amber-200 text-[clamp(11px,2.8vw,13px)] font-medium">
            {t.inputTypePending}
          </p>
        </div>
      )}

      {/* #493: 입력형 옵션 fully orderable UI (NUMBER_INPUT 수량 +/- / TEXT_INPUT / TEXTAREA) */}
      {isInputTypeOption && onInputChange && (
        <div className="mt-[clamp(8px,2vw,12px)]">
          {optionGroup.type === OptionType.NUMBER_INPUT && (
            <div className="flex items-center justify-center gap-3 bg-background rounded-xl border border-border p-3">
              <button
                type="button"
                onClick={() => {
                  const current = parseInt(inputValues?.[optionGroup.id] || '0', 10);
                  const next = Math.max(0, current - 1);
                  onInputChange(optionGroup.id, String(next));
                }}
                className="w-10 h-10 rounded-full bg-muted hover:bg-muted/80 flex items-center justify-center transition-colors"
                aria-label="감소"
              >
                <Minus className="w-4 h-4" />
              </button>
              <input
                type="number"
                min={validationRules?.min_value ?? 0}
                max={validationRules?.max_value}
                step="1"
                value={inputValues?.[optionGroup.id] ?? ''}
                onChange={(e) => onInputChange(optionGroup.id, e.target.value)}
                className="w-20 text-center text-lg font-medium bg-transparent border-b border-border focus:outline-none focus:border-primary"
                aria-label={optionGroupName}
              />
              <button
                type="button"
                onClick={() => {
                  const current = parseInt(inputValues?.[optionGroup.id] || '0', 10);
                  const next = current + 1;
                  onInputChange(optionGroup.id, String(next));
                }}
                className="w-10 h-10 rounded-full bg-primary hover:bg-primary/90 text-white flex items-center justify-center transition-colors"
                aria-label="증가"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          )}
          {optionGroup.type === OptionType.TEXT_INPUT && (
            <input
              type="text"
              value={inputValues?.[optionGroup.id] ?? ''}
              onChange={(e) => onInputChange(optionGroup.id, e.target.value)}
              minLength={validationRules?.min_length}
              maxLength={validationRules?.max_length ?? 200}
              pattern={validationRules?.pattern}
              className="w-full bg-background rounded-xl border border-border p-3 text-foreground focus:outline-none focus:border-primary"
              aria-label={optionGroupName}
            />
          )}
          {optionGroup.type === OptionType.TEXTAREA && (
            <textarea
              value={inputValues?.[optionGroup.id] ?? ''}
              onChange={(e) => onInputChange(optionGroup.id, e.target.value)}
              minLength={validationRules?.min_length}
              maxLength={validationRules?.max_length ?? 1000}
              rows={3}
              className="w-full bg-background rounded-xl border border-border p-3 text-foreground focus:outline-none focus:border-primary resize-y"
              aria-label={optionGroupName}
            />
          )}
        </div>
      )}

      {/* 옵션 리스트 (선택형만) */}
      {!isInputTypeOption && (
      <div className="space-y-[clamp(8px,2vw,12px)] min-[401px]:space-y-[10px]">
        {optionGroup.options.map((option) => {
          const selectedQuantity = currentSelections.filter(id => id === option.id).length;
          const isSelected = selectedQuantity > 0;
          const optionName = resolveCustomerDisplayName(
            option._name,
            t.options?.[option.nameKey as keyof typeof t.options],
            option.nameKey,
          ) ?? option.id;
          // 싱글 옵션(REQUIRED_SINGLE, OPTIONAL_SINGLE)에서는 최대 선택 제한을 무시
          const isSingleOption = optionGroup.type === OptionType.REQUIRED_SINGLE || optionGroup.type === OptionType.OPTIONAL_SINGLE;
          const quantityLimit = option.quantityLimit ?? Infinity;
          const isChoiceLimitReached = !isSingleOption && selectedQuantity >= quantityLimit;
          const isMaxReached = !isSingleOption && (selectedCount >= maxSelection || isChoiceLimitReached);

          {
            const isMultiple = optionGroup.type !== OptionType.REQUIRED_SINGLE && optionGroup.type !== OptionType.OPTIONAL_SINGLE;
            const shouldShowOverMaxStyle = isOverMax && isSelected;
            const rowClassName = `w-full flex items-center justify-between p-[clamp(12px,3vw,16px)] min-[401px]:p-[13px] rounded-xl transition-all duration-200 min-h-[clamp(19px,5vw,25px)] min-[401px]:min-h-[20px] ${
              isSelected
                ? shouldShowOverMaxStyle
                  ? 'bg-destructive/8 border-2 border-destructive shadow-sm'
                  : isRequiredOption
                    ? 'bg-secondary/8 border-2 border-secondary shadow-sm'
                    : 'bg-primary/8 border-2 border-primary shadow-sm'
                : isMaxReached
                  ? 'bg-muted border border-border opacity-60'
                  : 'bg-background border border-border hover:shadow-sm'
            }`;
            const rowStyle = {
              '--hover-border-color': optionThemeColor
            } as React.CSSProperties;
            const optionLabel = (
              <div className="flex items-center gap-[clamp(8px,2vw,12px)] min-[401px]:gap-[10px] flex-1 min-w-0">
                {/* 체크박스/라디오 버튼 */}
                <div
                  className={`w-[clamp(18px,4.5vw,22px)] min-[400px]:w-[22px] min-[401px]:w-[18px] h-[clamp(18px,4.5vw,22px)] min-[400px]:h-[22px] min-[401px]:h-[18px] flex items-center justify-center transition-colors shrink-0 border-2 relative ${
                    isMultiple ? 'rounded-[2px]' : 'rounded-full'
                  }`}
                  style={{
                    borderColor: isSelected
                      ? shouldShowOverMaxStyle ? 'var(--destructive)' : optionThemeColor
                      : optionThemeColor,
                    backgroundColor: isSelected
                      ? shouldShowOverMaxStyle ? 'var(--destructive)' : optionThemeColor
                      : 'transparent'
                  }}
                >
                  {isSelected && (
                    isMultiple ? (
                      <Check className="w-[clamp(10px,2.5vw,12px)] min-[400px]:w-[12px] min-[401px]:w-[10px] h-[clamp(10px,2.5vw,12px)] min-[400px]:h-[12px] min-[401px]:h-[10px] text-white absolute" />
                    ) : (
                      <div className="w-[clamp(6px,1.5vw,8px)] min-[400px]:w-[8px] min-[401px]:w-[6px] h-[clamp(6px,1.5vw,8px)] min-[400px]:h-[8px] min-[401px]:h-[6px] rounded-full bg-white absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
                    )
                  )}
                </div>
                <span className="text-[clamp(13px,3.2vw,16px)] min-[400px]:text-[16px] min-[401px]:text-[13px] font-medium text-foreground text-left">
                  {optionName}
                </span>
              </div>
            );
            const priceLabel = (
              <span className={`text-[clamp(12px,3vw,14px)] min-[400px]:text-[14px] min-[401px]:text-[11px] font-semibold shrink-0 ${
                isRequiredOption
                  ? 'text-secondary'
                  : option.additionalPrice > 0
                    ? 'text-primary'
                    : option.additionalPrice < 0
                      ? 'text-destructive'
                      : 'text-muted-foreground'
              }`}>
                {option.additionalPrice > 0 ? '+' : ''}{formatPrice(option.additionalPrice)}
              </span>
            );

            if (isMultiple) {
              return (
                <div
                  key={option.id}
                  className={rowClassName}
                  style={rowStyle}
                >
                  {optionLabel}

                  <div className="flex items-center gap-[clamp(8px,2vw,12px)] min-[401px]:gap-[10px]">
                    {priceLabel}
                    <div className="flex items-center gap-2 shrink-0">
                      <button
                        type="button"
                        onClick={() => onOptionDecrement?.(optionGroup, option.id)}
                        disabled={!isSelected}
                        className="w-8 h-8 rounded-full border border-border bg-background disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                        aria-label={`${optionName} 감소`}
                      >
                        <Minus className="w-4 h-4" />
                      </button>
                      <span className="w-5 text-center text-sm font-semibold tabular-nums">
                        {selectedQuantity}
                      </span>
                      <button
                        type="button"
                        onClick={() => onOptionSelect(optionGroup, option.id)}
                        disabled={isMaxReached}
                        className="w-8 h-8 rounded-full bg-primary text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
                        aria-label={`${optionName} 증가`}
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <button
                key={option.id}
                onClick={() => onOptionSelect(optionGroup, option.id)}
                disabled={isMaxReached}
                className={`${rowClassName} ${isMaxReached ? 'cursor-not-allowed' : 'active:scale-[0.98]'}`}
                style={rowStyle}
                onMouseEnter={(e) => {
                  if (!isSelected && !isMaxReached) {
                    (e.target as HTMLButtonElement).style.borderColor = optionThemeColor;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected && !isMaxReached) {
                    (e.target as HTMLButtonElement).style.borderColor = '';
                  }
                }}
              >
                {optionLabel}

                <div className="flex items-center gap-[clamp(8px,2vw,12px)] min-[401px]:gap-[10px]">
                  {/* #324: 추가 비용 0 옵션도 명시 — '+MX$0' 라벨로 사용자 직관 강화 */}
                  {priceLabel}


                  {/* 싱글 옵션 선택 시 체크마크 */}
                  {isSelected && !isMultiple && (
                    <div
                      className="w-[clamp(18px,4.5vw,22px)] min-[400px]:w-[22px] min-[401px]:w-[18px] h-[clamp(18px,4.5vw,22px)] min-[400px]:h-[22px] min-[401px]:h-[18px] rounded-full flex items-center justify-center shrink-0"
                      style={{ backgroundColor: optionThemeColor }}
                    >
                      <Check className="w-[clamp(10px,2.5vw,12px)] min-[400px]:w-[12px] min-[401px]:w-[10px] h-[clamp(10px,2.5vw,12px)] min-[400px]:h-[12px] min-[401px]:h-[10px] text-white" />
                    </div>
                  )}
                </div>
              </button>
            );
          }
        })}
      </div>
      )}

      {/* 선택 가이드 (선택형 옵션만) */}
      {!isInputTypeOption && (
      <div className="mt-[clamp(8px,2vw,12px)] min-[401px]:mt-[10px] p-[clamp(10px,2.5vw,12px)] min-[401px]:p-[10px] bg-muted rounded-lg">
        <div className="flex justify-between items-center text-[clamp(10px,2.5vw,12px)] min-[400px]:text-[12px] min-[401px]:text-[10px]">
          <span className="text-muted-foreground">
            {typeInfo.description}
          </span>
          <span className={`font-medium ${
            hasError
              ? 'text-destructive'
              : selectedCount >= minSelection && (maxSelection === 999 || selectedCount <= maxSelection)
              ? 'text-primary'
              : 'text-muted-foreground'
          }`}>
            {maxSelection === 999
              ? (t.selectionStatus?.selectedCount || '{count}개 선택됨').replace('{count}', selectedCount.toString())
              : `${selectedCount}/${maxSelection}`
            }
          </span>
        </div>
      </div>
      )}
    </div>
  );
}
